import type { AnalyticsSalesByCurrency } from "@/@types/Analytics";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Cell,
  Legend,
  Pie,
  PieChart as RechartsPieChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { CURRENCY_COLORS, PRIMARY_COLOR, SECONDARY_COLORS } from "../constants";
import { ChartEmpty } from "./ChartParts";

type CurrencyView = "chart" | "list";

const pieColor = (index: number) =>
  index === 0 ? PRIMARY_COLOR : SECONDARY_COLORS[index % SECONDARY_COLORS.length];

const listColor = (index: number) => CURRENCY_COLORS[index % CURRENCY_COLORS.length];

interface SalesByCurrencyCardProps {
  salesByCurrency?: AnalyticsSalesByCurrency[];
  isLoading: boolean;
}

export function SalesByCurrencyCard({ salesByCurrency, isLoading }: SalesByCurrencyCardProps) {
  const { t } = useTranslation("reports");
  const [view, setView] = useState<CurrencyView>("chart");
  const rows = useMemo(() => salesByCurrency ?? [], [salesByCurrency]);
  const maxCount = useMemo(() => Math.max(...rows.map((c) => c.count), 1), [rows]);

  const viewButton = (value: CurrencyView, label: string) => (
    <Button
      variant={view === value ? "default" : "outline"}
      size="sm"
      className={cn(
        "h-7 px-2 text-xs",
        view === value ? "bg-primary text-black hover:bg-primary/90" : "border-colorBorder",
      )}
      onClick={() => setView(value)}
    >
      {label}
    </Button>
  );

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-colorBorder">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-colorText truncate">
            {t("reports:chart_sales_by_currency")}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {t("reports:chart_sales_by_currency_desc")}
          </p>
        </div>
        <div className="flex gap-1 ml-4 flex-shrink-0">
          {viewButton("chart", t("reports:view_chart"))}
          {viewButton("list", t("reports:view_list"))}
        </div>
      </div>

      <div className="p-4">
        {isLoading ? (
          <Skeleton className="w-full h-[280px]" />
        ) : rows.length === 0 ? (
          <ChartEmpty className="h-[280px]" />
        ) : view === "chart" ? (
          <ResponsiveContainer width="100%" height={280}>
            <RechartsPieChart>
              <Pie
                data={rows.map((c, i) => ({
                  name: c.currency,
                  value: c.count,
                  amount_formatted: c.amount_formatted,
                  fill: pieColor(i),
                }))}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
              >
                {rows.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={pieColor(index)} />
                ))}
              </Pie>
              <RechartsTooltip
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;
                  const data = payload[0].payload;
                  return (
                    <div className="bg-zinc-900 border border-colorBorder px-3 py-2 rounded-lg">
                      <p className="text-sm font-medium text-colorText">{data.name}</p>
                      <p className="text-xs text-muted-foreground">{data.value} {t("reports:sales_unit")}</p>
                      <p className="text-xs text-primary">{data.amount_formatted}</p>
                    </div>
                  );
                }}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value) => (
                  <span className="text-xs text-muted-foreground">{value}</span>
                )}
              />
            </RechartsPieChart>
          </ResponsiveContainer>
        ) : (
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {rows.map((currency, index) => (
              <div key={currency.currency + index} className="relative group">
                <div
                  className="absolute left-0 top-0 h-full rounded-lg transition-all duration-300 opacity-20"
                  style={{
                    width: `${(currency.count / maxCount) * 100}%`,
                    backgroundColor: listColor(index),
                  }}
                />
                <div
                  className="absolute left-0 top-0 h-full w-1 rounded-l-lg"
                  style={{ backgroundColor: listColor(index) }}
                />
                <div className="relative flex items-center justify-between p-3 rounded-lg z-10">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="text-lg font-bold flex-shrink-0"
                      style={{ color: listColor(index) }}
                    >
                      {currency.currency_symbol}
                    </span>
                    <div className="min-w-0">
                      <span className="text-white text-xs font-medium block truncate">
                        {currency.currency}
                      </span>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {currency.currency_name}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <div className="text-white text-xs font-medium">
                      {currency.count} ({currency.count_percentage?.toFixed(1)}%)
                    </div>
                    <div className="text-primary text-xs font-medium">
                      {currency.amount_formatted}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
